import React, { useEffect, useState } from 'react';
import { Title, useTranslate } from 'react-admin';
import { useNavigate } from 'react-router-dom';
import LearningPathList from './LearningPathList';
import LearningPathDetail from './LearningPathDetail';
import CreateAiLearningPathDialog from './CreateAiLearningPathDialog';
import { aiLearningPathService } from '../../services/aiLearningPathService';
import './learning-paths-design.css';

/**
 * Lernpfade-Seite (/learning-paths) im Design-System-Look.
 * Die App-Shell (AppBar/Drawer/BottomNav) und die Seiten-<h1> kommen aus
 * Layout.tsx (.page + TitleBar) — hier wird nur der Seiteninhalt gerendert.
 */
const LearningPathsPage: React.FC = () => {
    const translate = useTranslate();
    const navigate = useNavigate();
    const [tabValue, setTabValue] = useState(0);
    // #519: "Lernpfad mit KI erstellen" — shown once the feature is confirmed available
    const [aiAvailable, setAiAvailable] = useState(false);
    const [aiOpen, setAiOpen] = useState(false);
    const [listKey, setListKey] = useState(0);
    useEffect(() => {
        let cancelled = false;
        aiLearningPathService.status().then((s) => { if (!cancelled) setAiAvailable(s.enabled); }).catch(() => undefined);
        return () => { cancelled = true; };
    }, []);
    const [selectedPathId, setSelectedPathId] = useState<number | null>(null);

    const handlePathSelect = (pathId: number) => {
        setSelectedPathId(pathId);
    };

    const handleBack = () => {
        setSelectedPathId(null);
    };

    const handleCourseSelect = (courseId: number) => {
        navigate(`/course-player/${courseId}`);
    };

    if (selectedPathId) {
        return (
            <div className="oa-learning-paths">
                <Title title={translate('learningPaths.title', { _: 'Lernpfade' })} />
                <LearningPathDetail
                    pathId={selectedPathId}
                    onBack={handleBack}
                    onCourseSelect={handleCourseSelect}
                />
            </div>
        );
    }

    return (
        <div className="oa-learning-paths">
            <Title title={translate('learningPaths.title', { _: 'Lernpfade' })} />

            <p className="lp-sub">Strukturierte Kursfolgen — vom Einstieg bis zur Spezialisierung.</p>
            {aiAvailable && (
                <div className="lp-ai-bar">
                    <button type="button" className="btn-primary" data-testid="create-ai-path" onClick={() => setAiOpen(true)}>
                        {translate('learningPaths.ai.button', { _: 'Lernpfad mit KI erstellen' })}
                    </button>
                    <span className="lp-ai-hint">{translate('learningPaths.ai.pageHint', { _: 'Du sagst der KI, was du lernen willst – sie stellt dir eine passende Kursfolge zusammen.' })}</span>
                </div>
            )}
            <CreateAiLearningPathDialog open={aiOpen} onClose={() => setAiOpen(false)} onCreated={(created) => { setAiOpen(false); setListKey((k) => k + 1); setTabValue(1); navigate(`/learning-paths/${created.id}`); }} />

            <div className="ktabs" role="tablist">
                <button
                    type="button"
                    role="tab"
                    aria-selected={tabValue === 0}
                    className={`ktab${tabValue === 0 ? ' active' : ''}`}
                    onClick={() => setTabValue(0)}
                >
                    {translate('learningPaths.allPaths', { _: 'Alle Lernpfade' })}
                </button>
                <button
                    type="button"
                    role="tab"
                    aria-selected={tabValue === 1}
                    className={`ktab${tabValue === 1 ? ' active' : ''}`}
                    onClick={() => setTabValue(1)}
                >
                    {translate('learningPaths.myPaths', { _: 'Meine Lernpfade' })}
                </button>
            </div>

            {tabValue === 0 ? (
                <LearningPathList key={`all-${listKey}`} onPathSelect={handlePathSelect} />
            ) : (
                <LearningPathList key={`mine-${listKey}`} onPathSelect={handlePathSelect} showOnlyEnrolled />
            )}
        </div>
    );
};

export default LearningPathsPage;
